/**
 * station_client.ts — station services on the client (space campaign K, 2026-10-01): one subscription per connection,
 * guarded readers (a pre-publish server, a bed, a churning cache never throw), the flag, and the ONE place a completed
 * station repair reaches the flight pools (`bindRepairSink` — SpaceModule registers it when the dock opens; a job's
 * receipt is applied once). Nothing here decides anything the server already decided (THE DRY-CHAIN LAW).
 */
import { atlas } from '../state.js';
import { STATION_SERVICES_FLAG, depthUnitsAt, refineEffAt } from '@shared/station_services.js';

interface Id { isEqual(o: unknown): boolean; toHexString(): string }
interface Ts { microsSinceUnixEpoch: bigint }
export interface ServiceRow {
  stationKey: string; role: string; berths: number; busy: number; source: string; operatorFirmId: bigint; partsAvail: number;
  reliefStock: number; reliefCap: number; reliefHour: bigint; budgetDay: bigint; budgetUsed: number; outage: string; numbersJson: string;
  changedAt: Ts; checkedAt: Ts;
}
export interface QuoteRow {
  quoteId: bigint; owner: Id; stationKey: string; role: string; kind: string; version: number; shipId: bigint; fitRev: string; wreckId: bigint;
  hpMax: number; missingHp: number; restoreHp: number; missiles: number; counters: number; parts: number; source: string; partUnitSov: bigint;
  partsSov: bigint; labourSov: bigint; rearmSov: bigint; operatorFeeSov: bigint; platformFeeSov: bigint; totalSov: bigint; durationS: number;
  mustered: boolean; unitsJson: string; note: string; state: string; createdAt: Ts; expiresAt: Ts;
}
export interface JobRow {
  jobId: bigint; quoteId: bigint; requestId: string; owner: Id; stationKey: string; role: string; kind: string; version: number; shipId: bigint;
  fitRev: string; wreckId: bigint; restoreHp: number; missiles: number; counters: number; parts: number; source: string; supplier: Id; engineer: Id;
  operator: Id; partsSov: bigint; labourSov: bigint; rearmSov: bigint; operatorFeeSov: bigint; platformFeeSov: bigint; totalSov: bigint;
  state: string; acceptedAt: Ts; readyAt: Ts; endedAt: Ts; receiptJson: string; note: string;
}
export interface MusterRow { key: string; cyc: bigint; identity: Id; civ: string; stationKey: string; hull: string; at: Ts }
export interface PlanRowDb {
  firmId: bigint; epoch: bigint; cyc: bigint; chosen: string; toKey: string; qty: number; netSov: bigint; perMinX100: bigint; pLossX1000: number;
  pLossLoX1000: number; pLossHiX1000: number; lost: number; arrived: number; rowsJson: string; stopReason: string; shipmentId: bigint; decidedAt: Ts;
}
export interface ConsignRow { consignId: bigint; shipmentId: bigint; firmId: bigint; orderId: bigint; buyer: Id; stationKey: string; familyKey: string; priceSov: bigint; qty: number; escrowSov: bigint; state: string; createdAt: Ts; updatedAt: Ts }
interface Db {
  stationService?: { iter(): Iterable<ServiceRow> };
  serviceQuote?: { iter(): Iterable<QuoteRow> };
  serviceJob?: { iter(): Iterable<JobRow>; onUpdate?(cb: (ctx: unknown, old: JobRow, row: JobRow) => void): void; onInsert?(cb: (ctx: unknown, row: JobRow) => void): void };
  stationMuster?: { iter(): Iterable<MusterRow> };
  depotResupply?: { iter(): Iterable<{ structId: bigint; owner: Id; lastAt: Ts; uses: number }> };
  firmPlan?: { iter(): Iterable<PlanRowDb> };
  firmConsignment?: { iter(): Iterable<ConsignRow> };
  civTreaty?: { iter(): Iterable<{ treatyId: bigint; kind: string; state: string; proposedBy: string; fromCyc: bigint; untilCyc: bigint }> };
  spaceFirm?: { iter(): Iterable<{ firmId: bigint; name: string; civ: string; kind: string; stationKey: string; hulls: number; hullsTarget: number }> };
  shipWreck?: { iter(): Iterable<{ wreckId: bigint; victim: Id; victimName: string; systemId: string; expiresAt: Ts; chassisKey: string }> };
  cargoLot?: { iter(): Iterable<{ lotId: bigint; owner: Id; familyKey: string; name: string; qty: number; locKind: string; locId: string; tag: string }> };
  pilotState?: { iter(): Iterable<{ identity: Id; factionKey: string }> };
  platformFlag?: { key: { find(k: string): { enabled: boolean } | undefined } };
  shipSubsystem?: { iter(): Iterable<{ shipId: bigint; engine: number; weapons: number }> };
}
const db = (): Db | null => (atlas.conn ? (atlas.conn.db as unknown as Db) : null);
export const safe = <T>(f: () => T, fb: T): T => { try { return f(); } catch { return fb; } };
export const msOf = (t: Ts | undefined): number => (t ? Number(t.microsSinceUnixEpoch / 1000n) : 0);
export function me(): Id | null { return (atlas.conn?.identity as unknown as Id) ?? null; }

let subscribed = false;
/** Subscribe the slice's rows once per connection (beside the sector's own handles — harmless). */
export function ensureStationRows(): void {
  const conn = atlas.conn as unknown as { subscriptionBuilder?: () => { subscribe(q: string[]): unknown } } | null;
  if (!conn?.subscriptionBuilder || subscribed) return;
  subscribed = true;
  try {
    conn.subscriptionBuilder().subscribe([
      'SELECT * FROM station_service', 'SELECT * FROM service_quote WHERE owner = :sender', 'SELECT * FROM service_job WHERE owner = :sender',
      'SELECT * FROM station_muster', 'SELECT * FROM depot_resupply WHERE owner = :sender', 'SELECT * FROM firm_plan', 'SELECT * FROM firm_consignment',
      'SELECT * FROM civ_treaty', 'SELECT * FROM space_firm', 'SELECT * FROM ship_wreck', 'SELECT * FROM cargo_lot WHERE owner = :sender',
      'SELECT * FROM pilot_state WHERE identity = :sender', 'SELECT * FROM platform_flag', 'SELECT * FROM ship_subsystem WHERE owner = :sender',
    ]);
  } catch { subscribed = false; /* pre-publish server */ }
}

/** The rollout flag as the shard holds it (ABSENT = ON, the server's flagOn(…, true)). */
export function stationServicesOnClient(): boolean {
  return safe(() => db()?.platformFlag?.key.find(STATION_SERVICES_FLAG)?.enabled ?? true, true);
}
/** The Exchange's depth units at a station, priced as the server prices them (the trade hub's difference). */
export function clientDepthUnits(stationKey: string, units: number): number { return depthUnitsAt(stationKey, units, stationServicesOnClient()); }
/** The refine line's yield where you are docked (the refinery's difference). */
export function clientRefineEff(stationKey: string): number { return refineEffAt(stationKey, stationServicesOnClient()); }

export function serviceRow(stationKey: string): ServiceRow | null { return safe(() => [...(db()?.stationService?.iter() ?? [])].find((r) => r.stationKey === stationKey) ?? null, null); }
export function heartbeatAgeS(nowMs = Date.now()): number {
  const r = serviceRow('*');
  return r ? Math.max(0, Math.round((nowMs - msOf(r.checkedAt)) / 1000)) : 0;
}
export function myQuote(stationKey: string, nowMs = Date.now()): QuoteRow | null {
  const m = me();
  return safe(() => [...(db()?.serviceQuote?.iter() ?? [])].filter((q) => m && q.owner.isEqual(m) && q.stationKey === stationKey && q.state === 'open' && msOf(q.expiresAt) > nowMs)
    .sort((a, b) => (a.quoteId < b.quoteId ? 1 : -1))[0] ?? null, null);
}
export function myJobs(): JobRow[] {
  const m = me();
  return safe(() => [...(db()?.serviceJob?.iter() ?? [])].filter((j) => m && j.owner.isEqual(m)).sort((a, b) => (a.jobId < b.jobId ? 1 : -1)), []);
}
export function musterRoster(cyc: bigint): MusterRow[] { return safe(() => [...(db()?.stationMuster?.iter() ?? [])].filter((r) => r.cyc === cyc), []); }
export function plans(): PlanRowDb[] { return safe(() => [...(db()?.firmPlan?.iter() ?? [])], []); }
export function consignments(): ConsignRow[] { return safe(() => [...(db()?.firmConsignment?.iter() ?? [])], []); }
export function treaties(): Array<{ treatyId: bigint; kind: string; state: string; proposedBy: string; fromCyc: bigint; untilCyc: bigint }> { return safe(() => [...(db()?.civTreaty?.iter() ?? [])], []); }
export function firms(): Array<{ firmId: bigint; name: string; civ: string; kind: string; stationKey: string; hulls: number; hullsTarget: number }> { return safe(() => [...(db()?.spaceFirm?.iter() ?? [])], []); }
export function myFaction(): string { const m = me(); return safe(() => [...(db()?.pilotState?.iter() ?? [])].find((p) => m && p.identity.isEqual(m))?.factionKey ?? '', ''); }
export function subsystemsOf(shipId: bigint): { engine: number; weapons: number } | null { return safe(() => { const r = [...(db()?.shipSubsystem?.iter() ?? [])].find((s) => s.shipId === shipId); return r ? { engine: r.engine, weapons: r.weapons } : null; }, null); }
/** Wrecks in a system holding lots I own (the wreck berth's candidates). */
export function myWrecksIn(systemId: string, nowMs = Date.now()): Array<{ wreckId: bigint; victimName: string; units: number; lots: number; expiresMs: number }> {
  const m = me();
  if (!m) return [];
  return safe(() => {
    const out: Array<{ wreckId: bigint; victimName: string; units: number; lots: number; expiresMs: number }> = [];
    for (const w of db()?.shipWreck?.iter() ?? []) {
      if (w.systemId !== systemId || msOf(w.expiresAt) <= nowMs) continue;
      let units = 0, lots = 0;
      for (const l of db()?.cargoLot?.iter() ?? []) if (l.locKind === 'wreck' && l.locId === `w:${w.wreckId}` && l.owner.isEqual(m)) { units += l.qty; lots++; }
      if (units > 0) out.push({ wreckId: w.wreckId, victimName: w.victimName, units, lots, expiresMs: msOf(w.expiresAt) });
    }
    return out;
  }, []);
}
export function myOreHere(stationKey: string, activeShipId: bigint | null): number {
  const m = me();
  return safe(() => {
    let n = 0;
    for (const l of db()?.cargoLot?.iter() ?? []) {
      if (!m || !l.owner.isEqual(m) || l.familyKey !== 'ferrite_ore' || l.tag) continue;
      if ((l.locKind === 'station' && l.locId === stationKey) || (activeShipId !== null && l.locKind === 'ship' && l.locId === `ship:${activeShipId}`)) n += l.qty;
    }
    return n;
  }, 0);
}

// ── THE REPAIR SINK: a completed station job reaches the flight pools ONCE ──────────────────────────────────────
export type RepairSink = (job: JobRow) => void;
let sink: RepairSink | null = null;
const applied = new Set<string>();
let hooked = false;
/** SpaceModule registers its pools here when the dock opens; a completed repair job (seen as an update or an insert of
 *  a `completed` row) is applied once per page — the hull's HP is the client's (gap 30), the receipt is the server's. */
export function bindRepairSink(fn: RepairSink): void {
  sink = fn;
  const t = db()?.serviceJob;
  if (!t || hooked) return;
  hooked = true;
  const consider = (row: JobRow, wasWorking: boolean): void => {
    const m = me();
    if (!m || !row.owner.isEqual(m) || row.kind !== 'repair' || row.state !== 'completed' || !wasWorking) return;
    const k = row.jobId.toString();
    if (applied.has(k)) return;
    applied.add(k);
    try { sink?.(row); } catch { /* the pools are best effort */ }
  };
  safe(() => { t.onUpdate?.((_c, old, row) => consider(row, old.state === 'working')); return 0; }, 0);
}
/** For harnesses and beds: has this job's receipt reached the pools? */
export function repairApplied(jobId: bigint): boolean { return applied.has(jobId.toString()); }
